import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/shared/hooks/use-toast";
import { SEO } from "@/shared/components/SEO";
import { trackViewContent, trackSchedule } from "@/shared/utils/metaPixel";
import {
  Play,
  Users,
  Calendar,
  CheckCircle,
  ArrowRight,
  Quote,
  Package,
  MapPin,
  Video,
  ShieldCheck,
  Zap,
  Home,
  Sparkles,
  Wrench,
  Wifi,
  Hammer,
  HardHat,
  Pen,
  FileText,
  FileCheck,
  MessageCircle,
  Camera,
  Pause,
  ChevronLeft,
  ChevronRight,
  Star
} from "lucide-react";
import { FaCar } from "react-icons/fa";

export default function LandingPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeSection, setActiveSection] = useState("how-it-works");
  const [hoveredFeature, setHoveredFeature] = useState<string | null>(null);
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);

  // Track ViewContent for Meta Pixel on page load
  useEffect(() => {
    trackViewContent({
      contentName: 'Landing Page',
      contentCategory: 'Home',
    });
  }, []);

  // Capture referral code from URL and store in localStorage
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    // Check for referral code - support multiple parameter names
    const refCode = urlParams.get('ref') ||
                    urlParams.get('referralCode') ||
                    urlParams.get('referral_code') ||
                    urlParams.get('code');

    if (refCode) {
      console.log('[LandingPage] 📎 Referral code detected:', refCode);
      localStorage.setItem('pendingReferralCode', refCode);
    }
  }, []);

  // Redirect authenticated users to dashboard or profile setup
  useEffect(() => {
    const checkAuthAndRedirect = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          console.log('[LandingPage] Session found, checking profile status...');

          // Check if profile is complete via backend API
          try {
            const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';
            const response = await fetch(`${API_URL}/api/user/me`, {
              headers: {
                'Authorization': `Bearer ${session.access_token}`
              }
            });

            if (response.ok) {
              const userData = await response.json();
              const user = userData?.user;
              console.log('[LandingPage] Profile status:', {
                profile_setup_complete: user?.profile_setup_complete,
                hasCompany: !!userData?.company,
                hasPersonalDetails: !!(user?.first_name && user?.last_name && user?.phone)
              });

              // Check if profile is TRULY complete (not just auto-created by trigger)
              // Must have: profile_setup_complete=true, company exists, AND personal details filled
              const isProfileComplete = user?.profile_setup_complete &&
                                       userData?.company &&
                                       user?.first_name &&
                                       user?.last_name &&
                                       user?.phone;

              if (isProfileComplete) {
                console.log('[LandingPage] ✅ Profile complete - redirecting to dashboard');
                navigate('/dashboard');
              } else {
                console.log('[LandingPage] ❌ Profile incomplete - redirecting to profile-setup');
                navigate('/profile-setup');
              }
            } else if (response.status === 401 || response.status === 404) {
              // Session expired or user deleted - clear session and stay on landing
              console.log('[LandingPage] Session invalid - clearing and staying on landing');
              await supabase.auth.signOut();
              localStorage.clear();
            } else {
              // Other errors - redirect to profile setup to be safe
              console.log('[LandingPage] Profile check failed - redirecting to profile-setup');
              navigate('/profile-setup');
            }
          } catch (error) {
            console.error('[LandingPage] Profile check error:', error);
            // On error, redirect to profile setup
            navigate('/profile-setup');
          }
        }
      } catch (error) {
        console.error('[LandingPage] Auth check error:', error);
      }
    };
    checkAuthAndRedirect();
  }, [navigate]);

  // Initialize Google One Tap on landing page
  useEffect(() => {
    const WEB_CLIENT_ID = '9332353701-nbub4mr46dd52qa54l2ncrnsf724kpuj.apps.googleusercontent.com';

    const initGoogleOneTap = () => {
      if ((window as any).google) {
        (window as any).google.accounts.id.initialize({
          client_id: WEB_CLIENT_ID,
          callback: async (response: any) => {
            try {
              const idToken = response.credential;
              if (!idToken) throw new Error('No ID token received');

              console.log('Landing Page - One Tap: Received ID token from Google');

              const { data, error } = await supabase.auth.signInWithIdToken({
                provider: 'google',
                token: idToken,
              });

              if (error) {
                if (error.message?.includes('already in use')) {
                  toast({
                    title: "Account Already Exists",
                    description: "This email is already registered. Redirecting to login...",
                    variant: "destructive",
                  });
                  setTimeout(() => navigate('/auth'), 1500);
                } else {
                  throw error;
                }
              } else {
                console.log('Landing Page - One Tap: Google sign-in successful');

                // CRITICAL FIX: Check profile status before redirecting
                try {
                  const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';
                  const session = data?.session;

                  if (!session) {
                    console.error('[LandingPage OneTap] No session after sign-in');
                    navigate('/profile-setup');
                    return;
                  }

                  console.log('[LandingPage OneTap] Checking profile status...');
                  const profileResponse = await fetch(`${API_URL}/api/user/me`, {
                    headers: {
                      'Authorization': `Bearer ${session.access_token}`
                    }
                  });

                  if (profileResponse.ok) {
                    const userData = await profileResponse.json();
                    const user = userData?.user;
                    console.log('[LandingPage OneTap] Profile check:', {
                      profile_setup_complete: user?.profile_setup_complete,
                      hasCompany: !!userData?.company,
                      hasPersonalDetails: !!(user?.first_name && user?.last_name && user?.phone)
                    });

                    // Check if profile is TRULY complete (not just auto-created by trigger)
                    // Must have: profile_setup_complete=true, company exists, AND personal details filled
                    const isProfileComplete = user?.profile_setup_complete &&
                                             userData?.company &&
                                             user?.first_name &&
                                             user?.last_name &&
                                             user?.phone;

                    if (isProfileComplete) {
                      console.log('[LandingPage OneTap] ✅ Profile complete - going to dashboard');
                      toast({
                        title: "Welcome back!",
                        description: "Successfully signed in with Google.",
                      });
                      setTimeout(() => navigate('/dashboard'), 1000);
                    } else {
                      console.log('[LandingPage OneTap] ❌ Profile incomplete - going to profile-setup');
                      toast({
                        title: "Welcome!",
                        description: "Let's complete your profile setup.",
                      });
                      setTimeout(() => navigate('/profile-setup'), 1000);
                    }
                  } else {
                    // Profile check failed - default to profile setup
                    console.log('[LandingPage OneTap] Profile check failed - going to profile-setup');
                    toast({
                      title: "Welcome!",
                      description: "Let's set up your profile.",
                    });
                    navigate('/profile-setup');
                  }
                } catch (profileError) {
                  console.error('[LandingPage OneTap] Profile check error:', profileError);
                  // On error, go to profile setup to be safe
                  navigate('/profile-setup');
                }
              }
            } catch (err: any) {
              console.error('Landing Page - One Tap error:', err);
            }
          },
        });

        // Automatically show One Tap prompt
        (window as any).google.accounts.id.prompt();
      }
    };

    // Try to initialize immediately, or wait for script to load
    if ((window as any).google) {
      initGoogleOneTap();
    } else {
      const checkInterval = setInterval(() => {
        if ((window as any).google) {
          initGoogleOneTap();
          clearInterval(checkInterval);
        }
      }, 100);

      // Clear interval after 5 seconds
      setTimeout(() => clearInterval(checkInterval), 5000);
    }
  }, [navigate, toast]);

  // Mock testimonials data
  const testimonials = [
    {
      id: 1,
      name: "Sarah Johnson",
      role: "Plumbing Service Owner",
      company: "Johnson Plumbing Co.",
      content: "wlkthru has revolutionized how we assess plumbing issues. We can now diagnose problems remotely and only send a technician when absolutely necessary. Our efficiency has increased by 300%!",
      avatar: "/lovable-uploads/43889d83-9609-4a19-8e1b-93ee6cefb772.png"
    },
    {
      id: 2,
      name: "Mike Rodriguez",
      role: "Moving Company Manager",
      company: "Swift Movers",
      content: "Before wlkthru, we'd waste hours driving to give estimates. Now we can assess the entire scope of a move through video calls. Our quote accuracy has improved dramatically!",
      avatar: "/lovable-uploads/43889d83-9609-4a19-8e1b-93ee6cefb772.png"
    },
    {
      id: 3,
      name: "Lisa Chen",
      role: "Appliance Repair Specialist",
      company: "Fix-It Fast",
      content: "The ability to guide customers through troubleshooting steps via video calls has been a game-changer. We solve 40% of issues remotely now!",
      avatar: "/lovable-uploads/43889d83-9609-4a19-8e1b-93ee6cefb772.png"
    },
    {
      id: 4,
      name: "David Thompson",
      role: "Garage Door Technician",
      company: "Thompson Doors",
      content: "wlkthru lets us evaluate door mechanisms and safety features remotely. We can determine the exact parts needed before arriving on-site. Incredible efficiency boost!",
      avatar: "/lovable-uploads/43889d83-9609-4a19-8e1b-93ee6cefb772.png"
    },
    {
      id: 5,
      name: "Amanda Wilson",
      role: "Handywoman",
      company: "Wilson Home Services",
      content: "Seeing the project scope clearly through video calls means I bring the right tools on the first visit every time. No more second trips!",
      avatar: "/lovable-uploads/43889d83-9609-4a19-8e1b-93ee6cefb772.png"
    },
    {
      id: 6,
      name: "Robert Kim",
      role: "Smart Home Installer",
      company: "Smart Solutions Pro",
      content: "Walking customers through setup and troubleshooting remotely has eliminated 70% of our house calls. Customers love the instant support!",
      avatar: "/lovable-uploads/43889d83-9609-4a19-8e1b-93ee6cefb772.png"
    },
    {
      id: 7,
      name: "Jennifer Martinez",
      role: "Cleaning Service Owner",
      company: "Spotless Cleaning Co.",
      content: "We can now assess cleaning requirements and provide accurate time estimates for any space without visiting first. Our scheduling is so much more efficient!",
      avatar: "/lovable-uploads/43889d83-9609-4a19-8e1b-93ee6cefb772.png"
    },
    {
      id: 8,
      name: "Steve Anderson",
      role: "Manufacturing Supervisor",
      company: "TechParts Industries",
      content: "Equipment troubleshooting through video calls has reduced our downtime by 60%. The visual assistance feature is incredibly valuable for our operations!",
      avatar: "/lovable-uploads/43889d83-9609-4a19-8e1b-93ee6cefb772.png"
    },
    {
      id: 9,
      name: "Maria Garcia",
      role: "Construction Project Manager",
      company: "Garcia Construction",
      content: "Evaluating project sites remotely helps us provide preliminary estimates before mobilizing crews. It's saved us thousands in travel costs!",
      avatar: "/lovable-uploads/43889d83-9609-4a19-8e1b-93ee6cefb772.png"
    },
    {
      id: 10,
      name: "John Davis",
      role: "Insurance Claims Adjuster",
      company: "SecureLife Insurance",
      content: "Documenting claims with live video evidence has streamlined our process tremendously. We can process claims 50% faster while reducing fraud!",
      avatar: "/lovable-uploads/43889d83-9609-4a19-8e1b-93ee6cefb772.png"
    }
  ];

  const nextTestimonials = () => {
    setCurrentTestimonialIndex((prev) => 
      prev + 1 >= testimonials.length - 2 ? 0 : prev + 1
    );
  };

  const prevTestimonials = () => {
    setCurrentTestimonialIndex((prev) => 
      prev - 1 < 0 ? testimonials.length - 3 : prev - 1
    );
  };

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLogin = () => {
    // Preserve referral code in URL if it exists
    const referralCode = localStorage.getItem('pendingReferralCode');
    const authUrl = referralCode ? `/auth?referralCode=${referralCode}` : '/auth';
    navigate(authUrl, { state: { from: '/' } });
  };

  const handleGetStarted = () => {
    // Preserve referral code in URL if it exists
    const referralCode = localStorage.getItem('pendingReferralCode');
    const authUrl = referralCode
      ? `/auth?mode=signup&referralCode=${referralCode}`
      : '/auth?mode=signup';
    navigate(authUrl, { state: { from: '/' } });
  };

  const handleGetDemo = () => {
    // Track Schedule event for Meta Pixel (demo scheduling intent)
    trackSchedule({
      email: '', // Anonymous user on landing page
    }, 'Demo Request');
    window.open('https://api.leadconnectorhq.com/widget/booking/9yoGqzJkpywxCy012LYn', '_blank');
  };

  return (
    <>
      <SEO
        title="Wlkthru - Video Calling for Service Professionals"
        description="wlkthru lets service pros offer video calls before driving out in person. Get fast answers, save miles, close more deals, and invoice with next-day payouts."
        canonical="https://www.wlkthru.co/"
        keywords="wlkthru, wlkthru.co, video calling, remote inspection, contractor video calls, professional video platform, service professionals"
      />
      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-b border-border z-50">
        <div className="container mx-auto px-10 py-2">
          <div className="flex items-center justify-between">
            {/* Logo and left navigation */}
            <div className="flex items-center -ml-4 md:ml-0">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="flex items-center gap-2 text-xs md:text-2xl font-bold text-primary hover:text-primary/90 transition-colors"
              >
              
                wlkthru
              </button>
              
              {/* Left navigation - next to logo */}
              <nav className="hidden md:flex space-x-8 ml-8">
                <button
                  onClick={() => {
                    navigate('/features');
                    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                  }}
                  className="text-sm font-medium transition-colors hover:text-primary text-foreground"
                >
                  Features
                </button>
                <button
                  onClick={() => {
                    navigate('/pricing');
                    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                  }}
                  className="text-sm font-medium transition-colors hover:text-primary text-foreground"
                >
                  Pricing
                </button>
              </nav>
              
              {/* Mobile navigation - next to logo */}
              <nav className="md:hidden flex space-x-3 ml-3">
                <button
                  onClick={() => {
                    navigate('/features');
                    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                  }}
                  className="text-xs font-medium transition-colors hover:text-primary text-foreground"
                >
                  Features
                </button>
                <button
                  onClick={() => {
                    navigate('/pricing');
                    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                  }}
                  className="text-xs font-medium transition-colors hover:text-primary text-foreground"
                >
                  Pricing
                </button>
              </nav>
            </div>

            {/* Mobile Navigation */}
            <div className="md:hidden flex items-center">
              <button 
                onClick={handleLogin}
                className="text-xs text-muted-foreground px-2 py-1.5 hover:text-foreground hover:shadow-lg hover:shadow-primary/20 hover:scale-105 active:scale-95 transition-all duration-200 cursor-pointer whitespace-nowrap rounded-full"
              >
                Log in
              </button>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4">
              <button 
                onClick={handleLogin}
                className="text-sm text-muted-foreground px-3 py-2 hover:text-foreground hover:shadow-lg hover:shadow-primary/20 hover:scale-105 active:scale-95 transition-all duration-200 cursor-pointer whitespace-nowrap rounded-full"
              >
                Log in
              </button>
              <button 
                onClick={handleGetStarted}
                className="text-sm px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-xl hover:shadow-primary/30 hover:scale-110 hover:brightness-110 active:scale-95 active:shadow-primary/50 rounded-md transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Get started for free
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-20 bg-gradient-to-b from-background to-primary/25">
        <div className="container mx-auto px-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-foreground leading-tight mb-8 animate-slide-in-right">
              How many bids do you drive to that{" "}
              <span className="text-primary">go nowhere?</span>
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed mb-12 max-w-3xl mx-auto">
              wlkthru lets you see the job before you show up — live video calls, recorded walkthroughs, and remote estimates so you only drive when it's worth it.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <Button onClick={handleGetDemo} variant="cta-primary" size="lg" className="text-lg px-8 py-3">
                Schedule a Free Demo
              </Button>
            </div>

            {/* Hero Visual */}
            <div className="relative">
              <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-3xl p-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card className="p-4">
                    <div className="h-20 bg-gradient-to-r from-primary/20 to-primary/5 rounded-lg flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-primary">$750</div>
                        <div className="text-xs text-muted-foreground">per month</div>
                        <div className="text-sm font-medium mt-2">Saved in vehicle expenses</div>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4">
                    <div className="h-20 bg-gradient-to-br from-secondary/20 to-secondary/5 rounded-lg flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-secondary">5-10</div>
                        <div className="text-xs text-muted-foreground">more estimates</div>
                        <div className="text-sm font-medium mt-2">per day</div>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4">
                    <div className="h-20 bg-gradient-to-br from-accent/20 to-accent/5 rounded-lg flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-accent">1,650</div>
                        <div className="text-xs text-muted-foreground">fewer miles driven</div>
                        <div className="text-sm font-medium mt-2">per month on average</div>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is wlkthru Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Main Page Image */}
              <div className="rounded-2xl aspect-video overflow-hidden shadow-lg">
                <img
                  src="/Main Page Pic.png"
                  alt="wlkthru platform overview"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Content */}
              <div className="text-center lg:text-left">
                <h2 className="text-4xl font-bold text-foreground mb-6">
                  <span className="text-primary">wlkthru</span>
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                  Stop driving to every job site just to give an estimate, or spending hours on the phone trying to understand what you can't see. 
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                  wlkthru lets you see what your customer sees through live video calls and recorded videos, so you can assess jobs, understand scale of a move, and provide accurate quotes remotely.
                </p>
                <div className="flex justify-center lg:justify-start">
                  <Button onClick={handleGetDemo} variant="cta-primary" className="px-8 py-3 text-lg">
                    Get a demo
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* wlkthru for every industry Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-10">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-center text-foreground mb-4">
              wlkthru for <span className="text-primary">Every Industry</span>
            </h2>
            
            {/* Constantly scrolling carousel */}
            <div className="mt-12 overflow-hidden">
              <div className="animate-[scroll-seamless_15s_linear_infinite] md:animate-[scroll-seamless_30s_linear_infinite] flex items-center whitespace-nowrap">
                <span className="text-2xl font-medium text-foreground">Plumbing</span>
                <span className="mx-3 text-muted-foreground">•</span>
                <span className="text-2xl font-medium text-foreground">Moving</span>
                <span className="mx-3 text-muted-foreground">•</span>
                <span className="text-2xl font-medium text-foreground">Appliance Repair</span>
                <span className="mx-3 text-muted-foreground">•</span>
                <span className="text-2xl font-medium text-foreground">Garage Door</span>
                <span className="mx-3 text-muted-foreground">•</span>
                <span className="text-2xl font-medium text-foreground">Handy Man</span>
                <span className="mx-3 text-muted-foreground">•</span>
                <span className="text-2xl font-medium text-foreground">Smart Home</span>
                <span className="mx-3 text-muted-foreground">•</span>
                <span className="text-2xl font-medium text-foreground">Cleaning</span>
                <span className="mx-3 text-muted-foreground">•</span>
                <span className="text-2xl font-medium text-foreground">Manufacturing</span>
                <span className="mx-3 text-muted-foreground">•</span>
                <span className="text-2xl font-medium text-foreground">Construction</span>
                <span className="mx-3 text-muted-foreground">•</span>
                <span className="text-2xl font-medium text-foreground">Insurance</span>
                <span className="mx-3 text-muted-foreground">•</span>
                <span className="text-2xl font-medium text-foreground">Automotive</span>
                <span className="mx-3 text-muted-foreground">•</span>
                <span className="text-2xl font-medium text-foreground">Junk Removal</span>
                <span className="mx-3 text-muted-foreground">•</span>
                {/* Duplicate for seamless loop */}
                <span className="text-2xl font-medium text-foreground">Plumbing</span>
                <span className="mx-3 text-muted-foreground">•</span>
                <span className="text-2xl font-medium text-foreground">Moving</span>
                <span className="mx-3 text-muted-foreground">•</span>
                <span className="text-2xl font-medium text-foreground">Appliance Repair</span>
                <span className="mx-3 text-muted-foreground">•</span>
                <span className="text-2xl font-medium text-foreground">Garage Door</span>
                <span className="mx-3 text-muted-foreground">•</span>
                <span className="text-2xl font-medium text-foreground">Handy Man</span>
                <span className="mx-3 text-muted-foreground">•</span>
                <span className="text-2xl font-medium text-foreground">Smart Home</span>
                <span className="mx-3 text-muted-foreground">•</span>
                <span className="text-2xl font-medium text-foreground">Cleaning</span>
                <span className="mx-3 text-muted-foreground">•</span>
                <span className="text-2xl font-medium text-foreground">Manufacturing</span>
                <span className="mx-3 text-muted-foreground">•</span>
                <span className="text-2xl font-medium text-foreground">Construction</span>
                <span className="mx-3 text-muted-foreground">•</span>
                <span className="text-2xl font-medium text-foreground">Insurance</span>
                <span className="mx-3 text-muted-foreground">•</span>
                <span className="text-2xl font-medium text-foreground">Automotive</span>
                <span className="mx-3 text-muted-foreground">•</span>
                <span className="text-2xl font-medium text-foreground">Junk Removal</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Virtually Connect Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-10">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-foreground mb-4">
                Virtually Connect with your <span className="text-primary">Customers First</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  Offer your customers a live video call, schedule one for later, or request a video recording.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Start Live Video Call */}
              <Card className="p-6 text-center transition-all duration-300 hover:shadow-xl hover:shadow-primary/30 hover:border-primary/30 hover:bg-gradient-to-br hover:from-background hover:to-primary/10 hover:scale-105">
                <div className="mb-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Video className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">Start a Live Video Call</h3>
                  <p className="text-muted-foreground mb-6">
                    Instantly connect with your customers via a live video two way video call.
                  </p>
                </div>
                <div className="rounded-lg overflow-hidden">
                  <img 
                    src="/lovable-uploads/e95a010b-0800-45e1-aea5-66aa652785e8.png" 
                    alt="Start Live Video Call Interface"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </Card>

              {/* Schedule Live Video Call */}
              <Card className="p-6 text-center transition-all duration-300 hover:shadow-xl hover:shadow-primary/30 hover:border-primary/30 hover:bg-gradient-to-br hover:from-background hover:to-primary/10 hover:scale-105">
                <div className="mb-6">
                  <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Calendar className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">Schedule a Video Call</h3>
                  <p className="text-muted-foreground mb-6">
                    Schedule a time for your call. Your customer gets a reminder text before the call, so you're not left hanging.
                  </p>
                </div>
                <div className="rounded-lg overflow-hidden">
                  <img 
                    src="/lovable-uploads/eeaa6371-5a57-461f-9871-6d5d1c8a6d76.png" 
                    alt="Schedule a Video Call Interface"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </Card>

              {/* Request a Video */}
              <Card className="p-6 text-center transition-all duration-300 hover:shadow-xl hover:shadow-primary/30 hover:border-primary/30 hover:bg-gradient-to-br hover:from-background hover:to-primary/10 hover:scale-105">
                <div className="mb-6">
                  <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Play className="h-8 w-8 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">Request a Recording</h3>
                  <p className="text-muted-foreground mb-6">
                    Easily send a link and let your customer record a video on their own schedule. Perfect for walkthroughs, estimates, or showing what needs fixing. No app needed.
                  </p>
                </div>
                <div className="rounded-lg overflow-hidden">
                  <img 
                    src="/lovable-uploads/510dcb72-2624-41bb-83f0-fa8b212e5875.png" 
                    alt="Request Recording Interface"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Timeline Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              {/* Left side - Text and Image */}
              <div className="space-y-8 lg:sticky lg:top-20">
                <div className="text-center lg:text-left">
                   <h2 className="text-4xl font-bold text-foreground mb-6">
                     Features that help you close deals faster and improve customer satisfaction
                   </h2>
                   <div className="flex justify-center lg:justify-start">
                     <Button 
                       onClick={() => {
                         navigate('/features');
                         setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                       }} 
                       variant="cta-primary"
                       className="px-6 py-3"
                     >
                       Explore more features
                     </Button>
                   </div>
                 </div>

                {/* Hero Image */}
                <div className="rounded-2xl overflow-hidden shadow-xl max-w-3xl mx-auto">
                  <img
                    src="/Pricing Image .png"
                    alt="wlkthru dashboard preview"
                    className="w-full h-auto object-contain"
                  />
                </div>
              </div>

              {/* Right side - Features Timeline */}
              <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border"></div>

                <div className="space-y-8">
                  {/* Two Way Video Calls */}
                  <div 
                    className="relative flex items-start gap-4"
                    onMouseEnter={() => setHoveredFeature('Two Way Video Calls')}
                    onMouseLeave={() => setHoveredFeature(null)}
                  >
                    <div className="relative z-10 w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Video className="h-8 w-8 text-blue-600" />
                    </div>
                    <div className="bg-background rounded-lg p-6 border border-border shadow-sm flex-1 transition-all duration-300 hover:shadow-xl hover:shadow-primary/30 hover:border-primary/30 hover:bg-gradient-to-br hover:from-background hover:to-primary/10 hover:scale-105 cursor-pointer">
                      <h3 className="text-xl font-bold text-foreground mb-2">Two Way Video Calls</h3>
                      <p className="text-muted-foreground">
                        Skip the drive. Diagnose the issue, give the estimate, and keep moving—while still giving your customers an exceptional experience.
                      </p>
                    </div>
                    <div className="absolute left-6 top-8 w-4 h-4 bg-blue-100 rounded-full border-2 border-blue-600"></div>
                  </div>

                  {/* Live Annotations */}
                  <div 
                    className="relative flex items-start gap-4"
                    onMouseEnter={() => setHoveredFeature('Live Annotations')}
                    onMouseLeave={() => setHoveredFeature(null)}
                  >
                    <div className="relative z-10 w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Pen className="h-8 w-8 text-purple-600" />
                    </div>
                    <div className="bg-background rounded-lg p-6 border border-border shadow-sm flex-1 transition-all duration-300 hover:shadow-xl hover:shadow-primary/30 hover:border-primary/30 hover:bg-gradient-to-br hover:from-background hover:to-primary/10 hover:scale-105 cursor-pointer">
                      <h3 className="text-xl font-bold text-foreground mb-2">Live Annotations</h3>
                      <p className="text-muted-foreground">
                        Adding notes and visuals during a call makes it easier for experts and customers to stay on the same page. With wlkthru, users can draw directly on the screen.
                      </p>
                    </div>
                    <div className="absolute left-6 top-8 w-4 h-4 bg-purple-100 rounded-full border-2 border-purple-600"></div>
                  </div>

                  {/* Automatic Session Summary */}
                  <div 
                    className="relative flex items-start gap-4"
                    onMouseEnter={() => setHoveredFeature('Automatic Session Summary')}
                    onMouseLeave={() => setHoveredFeature(null)}
                  >
                    <div className="relative z-10 w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <FileCheck className="h-8 w-8 text-green-600" />
                    </div>
                    <div className="bg-background rounded-lg p-6 border border-border shadow-sm flex-1 transition-all duration-300 hover:shadow-xl hover:shadow-primary/30 hover:border-primary/30 hover:bg-gradient-to-br hover:from-background hover:to-primary/10 hover:scale-105 cursor-pointer">
                      <h3 className="text-xl font-bold text-foreground mb-2">Automatic Session Summary</h3>
                      <p className="text-muted-foreground">
                        Get a post call summary with key moments as well as AI-powered full transcripts.
                      </p>
                    </div>
                    <div className="absolute left-6 top-8 w-4 h-4 bg-green-100 rounded-full border-2 border-green-600"></div>
                  </div>

                  {/* In Call Chat */}
                  <div 
                    className="relative flex items-start gap-4"
                    onMouseEnter={() => setHoveredFeature('In Call Chat')}
                    onMouseLeave={() => setHoveredFeature(null)}
                  >
                    <div className="relative z-10 w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="h-8 w-8 text-orange-600" />
                    </div>
                    <div className="bg-background rounded-lg p-6 border border-border shadow-sm flex-1 transition-all duration-300 hover:shadow-xl hover:shadow-primary/30 hover:border-primary/30 hover:bg-gradient-to-br hover:from-background hover:to-primary/10 hover:scale-105 cursor-pointer">
                      <h3 className="text-xl font-bold text-foreground mb-2">In Call Chat</h3>
                      <p className="text-muted-foreground">
                        Talking's great—but sometimes you just need to write it down. Use in-call chat to share notes, instructions, or anything else that's easier to text.
                      </p>
                    </div>
                    <div className="absolute left-6 top-8 w-4 h-4 bg-orange-100 rounded-full border-2 border-orange-600"></div>
                  </div>

                  {/* Snapshots */}
                  <div 
                    className="relative flex items-start gap-4"
                    onMouseEnter={() => setHoveredFeature('Snapshots')}
                    onMouseLeave={() => setHoveredFeature(null)}
                  >
                    <div className="relative z-10 w-16 h-16 bg-cyan-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Camera className="h-8 w-8 text-cyan-600" />
                    </div>
                    <div className="bg-background rounded-lg p-6 border border-border shadow-sm flex-1 transition-all duration-300 hover:shadow-xl hover:shadow-primary/30 hover:border-primary/30 hover:bg-gradient-to-br hover:from-background hover:to-primary/10 hover:scale-105 cursor-pointer">
                      <h3 className="text-xl font-bold text-foreground mb-2">Snapshots</h3>
                      <p className="text-muted-foreground">
                        Capture key moments during the video call with snapshots. Take still images of what the customer is showing so you can review, quote, or reference later.
                      </p>
                    </div>
                    <div className="absolute left-6 top-8 w-4 h-4 bg-cyan-100 rounded-full border-2 border-cyan-600"></div>
                  </div>

                  {/* Invoicing */}
                  <div
                    className="relative flex items-start gap-4"
                    onMouseEnter={() => setHoveredFeature('Invoicing')}
                    onMouseLeave={() => setHoveredFeature(null)}
                  >
                    <div className="relative z-10 w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <FileText className="h-8 w-8 text-teal-600" />
                    </div>
                    <div className="bg-background rounded-lg p-6 border border-border shadow-sm flex-1 transition-all duration-300 hover:shadow-xl hover:shadow-primary/30 hover:border-primary/30 hover:bg-gradient-to-br hover:from-background hover:to-primary/10 hover:scale-105 cursor-pointer">
                      <h3 className="text-xl font-bold text-foreground mb-2">Invoicing</h3>
                      <p className="text-muted-foreground">
                        Send invoices and get paid faster. Bill customers directly, track payments, and even charge transaction fees so you receive the full amount billed.
                      </p>
                    </div>
                    <div className="absolute left-6 top-8 w-4 h-4 bg-teal-100 rounded-full border-2 border-teal-600"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-background to-primary/25">
        <div className="container mx-auto px-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-foreground mb-4">
              Ready to transform your customer experience?
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Ready to stop driving to every job before you know if it's worth it?
            </p>
            <div className="flex justify-center">
              <Button
                onClick={handleGetDemo}
                variant="cta-primary"
                size="lg"
                className="text-lg px-8 py-4"
              >
                Schedule a Free Demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 pb-3 bg-primary">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-white text-center md:text-left">
            {/* Brand Section */}
            <div className="space-y-4">
              <button 
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-4xl font-bold hover:text-white/80 transition-colors cursor-pointer"
              >
                wlkthru
              </button>
              <div className="w-16 h-0.5 bg-white/30 mx-auto md:mx-0"></div>
              <div className="space-y-3">
                <p className="text-white/90">Check us out on:</p>
                <div className="flex gap-2 justify-center md:justify-start">
                  <a 
                    href="https://www.facebook.com/profile.php?id=61578600642587" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <span className="text-xs text-white">f</span>
                  </a>
                  <a 
                    href="https://www.instagram.com/wlkthru.co/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <span className="text-xs text-white">ig</span>
                  </a>
                  <a 
                    href="https://www.linkedin.com/company/wlkthruco/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <span className="text-xs text-white">in</span>
                  </a>
                  <a 
                    href="https://www.youtube.com/@wlkthruco" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <span className="text-xs text-white">yt</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Get in Touch and Legal */}
            <div className="flex flex-col md:flex-row gap-8 md:col-span-2 md:justify-end">
              {/* Get in Touch */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-white">Get in Touch</h4>
                <ul className="space-y-2">
                  <li><button onClick={() => { navigate('/contact'); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="text-white/80 hover:text-white transition-colors">• Contact Us</button></li>
                  <li><a href="https://api.leadconnectorhq.com/widget/booking/9yoGqzJkpywxCy012LYn" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors">• Demo Request</a></li>
                </ul>
              </div>

              {/* Legal */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-white">Legal</h4>
                <ul className="space-y-2">
                  <li><a href="/legal?section=privacy" className="text-white/80 hover:text-white transition-colors">• Privacy Policy</a></li>
                  <li><a href="/legal?section=terms" className="text-white/80 hover:text-white transition-colors">• Terms and Conditions</a></li>
                  <li><a href="/legal?section=disclaimer" className="text-white/80 hover:text-white transition-colors">• Disclaimer</a></li>
                </ul>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-8 pt-6 border-t border-white/20 text-center">
            <p className="text-white/60 text-xs font-light">
              Copyright © 2025 wlkthru | All rights reserved
            </p>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
}